import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Skeleton from "@mui/material/Skeleton";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import {updateGlobalFeatureFlags} from "../../api/configurationApi";
import type {GlobalFeatureFlags} from "../../types/configuration";
import type {TenantSummaryResponse} from "../../types/tenant";
import {GLOBAL_FEATURE_FLAGS} from "../../constants/featureFlags";
import {featureFlagLabel} from "../../constants/featureFlagLabels";
import {storedFlagValue} from "../../utils/featureFlagResolution";
import {colors} from "../../theme/tokens";
import {SectionCard} from "../ui/SectionCard";

type GlobalFeatureFlagsCardProps = {
    /** `null` while loading. */
    flags: GlobalFeatureFlags | null;
    /** Loaded tenant summaries, to show how many tenants inherit each flag; empty while unknown. */
    tenants: TenantSummaryResponse[];
    onSaved: (flags: GlobalFeatureFlags) => void;
    onError?: (message: string) => void;
};

function inheritingCount(tenants: TenantSummaryResponse[], flag: string): number {
    return tenants.filter((tenant) => storedFlagValue(tenant.featureConfig ?? {}, flag) === null).length;
}

export function GlobalFeatureFlagsCard({flags, tenants, onSaved, onError}: GlobalFeatureFlagsCardProps) {
    const {t} = useTranslation();
    // Pending edits on top of the loaded flags; deriving the shown values (instead of copying the flags into state
    // after they load) means the switches never render a frame with the wrong values.
    const [edits, setEdits] = useState<GlobalFeatureFlags>({});
    const formValues = useMemo<GlobalFeatureFlags>(() => ({...(flags ?? {}), ...edits}), [flags, edits]);
    const [saving, setSaving] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const changedFlags = useMemo(
            () => (flags ? GLOBAL_FEATURE_FLAGS.filter((key) => (formValues[key] ?? false) !== (flags[key] ?? false)) : []),
            [formValues, flags],
    );
    const hasChanges = changedFlags.length > 0;
    const stateLabel = (value: boolean) => (value ? t("FLAGS_ON") : t("FLAGS_OFF"));

    const handleConfirmSave = async () => {
        setConfirmOpen(false);
        setSaving(true);
        try {
            const updated = await updateGlobalFeatureFlags(formValues);
            setEdits({});
            onSaved(updated);
        } catch (e) {
            onError?.(e instanceof Error ? e.message : t("FLAGS_UPDATE_GLOBAL_ERROR"));
        } finally {
            setSaving(false);
        }
    };

    return (
            <>
                <SectionCard
                        flush
                        title={t("FLAGS_GLOBAL_DEFAULTS")}
                        helper={t("FLAGS_GLOBAL_HELPER", {count: GLOBAL_FEATURE_FLAGS.length})}
                >
                    <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)"}, gap: 0.5, p: 1.5}}>
                        {GLOBAL_FEATURE_FLAGS.map((key) => {
                            const checked = formValues[key] ?? false;
                            const changed = changedFlags.includes(key);
                            return flags ? (
                                    <Box
                                            key={key}
                                            sx={{px: 1, py: 0.75, borderRadius: "4px", backgroundColor: changed ? colors.infoTint : "transparent"}}
                                    >
                                        <FormControlLabel
                                                sx={{m: 0, alignItems: "flex-start"}}
                                                control={
                                                    <Switch
                                                            checked={checked}
                                                            onChange={(_, value) => setEdits((previous) => ({...previous, [key]: value}))}
                                                            sx={{mt: 0.25}}
                                                    />
                                                }
                                                label={
                                                    <Box>
                                                        <Box component="span" sx={{color: colors.ink}}>{featureFlagLabel(t, key)}</Box>
                                                        {tenants.length > 0 && (
                                                                <Typography component="span" sx={{display: "block", fontSize: "11px", color: colors.faint}}>
                                                                    {t("FLAGS_INHERITING", {count: inheritingCount(tenants, key), total: tenants.length})}
                                                                </Typography>
                                                        )}
                                                    </Box>
                                                }
                                        />
                                    </Box>
                            ) : (
                                    <Skeleton key={key} height={36} sx={{mx: 1}} />
                            );
                        })}
                    </Box>
                    <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                                px: 2,
                                py: 1.25,
                                borderTop: `1px solid ${colors.sectionDivider}`,
                                backgroundColor: colors.tableHeaderAlt,
                            }}
                    >
                        <Typography variant="body2" sx={{flex: 1, color: hasChanges ? colors.ink : colors.muted}}>
                            {hasChanges
                                    ? changedFlags.map((key) => `${featureFlagLabel(t, key)} → ${stateLabel(formValues[key] ?? false)}`).join(" · ")
                                    : t("FLAGS_NO_CHANGES")}
                        </Typography>
                        <Button variant="outlined" onClick={() => setEdits({})} disabled={!hasChanges || saving}>
                            {t("CANCEL")}
                        </Button>
                        <Button variant="contained" onClick={() => setConfirmOpen(true)} disabled={!hasChanges || saving}>
                            {t("FLAGS_REVIEW_AND_SAVE")}
                        </Button>
                    </Box>
                </SectionCard>

                <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>{t("FLAGS_UPDATE_GLOBAL_DIALOG_TITLE")}</DialogTitle>
                    <DialogContent>
                        <Typography sx={{mb: 1}}>{t("FLAGS_UPDATE_GLOBAL_CONFIRM_INTRO")}</Typography>
                        <Box component="ul" sx={{m: 0, pl: 2.5, mb: 1.5}}>
                            {changedFlags.map((key) => (
                                    <li key={key}>
                                        {featureFlagLabel(t, key)}: {formValues[key] ? t("FLAGS_ENABLED") : t("FLAGS_DISABLED")}
                                        {tenants.length > 0 && (
                                                <Box component="span" sx={{color: colors.muted}}>
                                                    {" "}({t("FLAGS_AFFECTS_INHERITING", {count: inheritingCount(tenants, key)})})
                                                </Box>
                                        )}
                                    </li>
                            ))}
                        </Box>
                        <Typography variant="body2" sx={{color: colors.muted}}>{t("FLAGS_UPDATE_GLOBAL_CONFIRM_AFFECTED")}</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={() => setConfirmOpen(false)}>{t("CANCEL")}</Button>
                        <Button onClick={() => void handleConfirmSave()} variant="contained">
                            {t("DETAIL_SAVE")}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
    );
}
